import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import {
  ColorOptionLabel,
  MiDetailFields,
  MiModal,
  MiModalConfirm,
} from "@miplatform/components";
import {
  BOARD_MODAL_MODE,
  BUTTON_COLORS,
  BUTTON_LABELS,
  BUTTON_VARIANTS,
  CONFIGURABLE_COLORS,
  INPUT_TYPES,
  MODAL_TITLES,
} from "@miplatform/constants";
import { createUUID } from "@miplatform/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { mdiAccount, mdiTrashCanOutline } from "@mdi/js";
import { UserAutocomplete } from "@mibuildweb/components";
import { useTheme } from "@mui/material";

const schema = z.object({
  name: z.string().min(1, "Name is required"),
  color: z.string().min(1, "Color is required"),
  dispatcher: z.string().min(1, "Dispatcher is required"),
});

export const BoardModal = ({
  open,
  handleClose,
  updateBoardConfig,
  selectedBoard = {},
  viewConfig,
}) => {
  const isCreateNewBoard = open === BOARD_MODAL_MODE.CREATE;
  const isUpdateBoard = open === BOARD_MODAL_MODE.UPDATE;
  const isDeleteBoard = open === BOARD_MODAL_MODE.DELETE;
  const {
    formState: { errors, isValid, isDirty },
    control,
    getValues,
    setValue,
    reset,
  } = useForm({
    mode: "onChange",
    defaultValues: {},
    resolver: zodResolver(schema),
  });
  const theme = useTheme();
  const closeModal = () => {
    handleClose();
    reset({});
  };

  const handleSubmit = () => {
    const boardData = {
      ...getValues(),
      dispatchers: [getValues("dispatcher")],
      id: isCreateNewBoard ? createUUID() : selectedBoard.id,
      users: isCreateNewBoard ? [] : selectedBoard.users,
      ...(isUpdateBoard && {
        modifiedOn: selectedBoard.modifiedOn,
      }),
    };
    const mode = isCreateNewBoard
      ? BOARD_MODAL_MODE.CREATE
      : BOARD_MODAL_MODE.UPDATE;

    updateBoardConfig(boardData, mode);
    closeModal();
  };

  const handleDelete = () => {
    updateBoardConfig(
      {
        id: selectedBoard.id,
        modifiedOn: selectedBoard.modifiedOn,
      },
      BOARD_MODAL_MODE.DELETE
    );
    closeModal();
  };

  const fields = [
    {
      label: "Board Name",
      name: "name",
      width: "100%",
      required: true,
      control,
    },
    {
      label: "Color",
      name: "color",
      required: true,
      fieldType: INPUT_TYPES.SELECT,
      options: CONFIGURABLE_COLORS.map(({ color, label }) => ({
        label: <ColorOptionLabel color={color} label={label} />,
        value: color,
      })),
      width: "100%",
      control,
    },
    {
      label: "Dispatcher",
      name: "dispatcher",
      fieldType: INPUT_TYPES.CUSTOM,
      formControl: control,
      width: "50%",
      customComponent: (
        <UserAutocomplete
          required
          label="Dispatcher"
          data-testId="mi-autocomplete-dispatcher"
          options={viewConfig.schedulerUsers}
          value={getValues("dispatcher")}
          error={errors.dispatcher}
          onChange={(value) => {
            setValue("dispatcher", value, {
              shouldValidate: true,
              shouldDirty: true,
            });
          }}
        />
      ),
    },
  ];

  useEffect(() => {
    if (isCreateNewBoard) {
      reset({
        name: "",
        color: "",
        dispatcher: null,
      });
    } else if (isUpdateBoard) {
      reset({
        name: selectedBoard.name || "",
        color: selectedBoard.color || "",
        dispatcher:
          (selectedBoard.dispatchers && selectedBoard.dispatchers[0]?.id) ||
          null,
      });
    }
  }, [open]);

  if (isDeleteBoard) {
    return (
      <MiModalConfirm
        open
        title={MODAL_TITLES.DELETE}
        testId="delete-board-modal"
        titleIcon={{
          path: mdiTrashCanOutline,
          color: theme.palette.error.main,
        }}
        description={`Are you sure you want to delete ${selectedBoard.name} board? This action cannot be undone.`}
        handlePositive={handleDelete}
        handleNegative={closeModal}
        textPositive={BUTTON_LABELS.DELETE}
        color={theme.palette.red.main}
      />
    );
  }

  return (
    <MiModal
      title={isCreateNewBoard ? "Create New Board" : "Update Board"}
      testId="create-new-board-modal"
      open={isCreateNewBoard || isUpdateBoard}
      description={
        isCreateNewBoard
          ? "Please enter the details to set up the new board"
          : "Please enter the details to update the board"
      }
      closeModal={closeModal}
      titleIcon={mdiAccount}
      leftIconColor={theme.palette.purple.main}
      actions={[
        {
          name: "Cancel",
          variant: BUTTON_VARIANTS.TEXT,
          size: "medium",
          color: BUTTON_COLORS.BLACK,
          onClick: closeModal,
        },
        {
          name: isCreateNewBoard ? "Create Board" : "Update Board",
          variant: BUTTON_VARIANTS.CONTAINED,
          size: "medium",
          disabled: !isValid || !isDirty,
          onClick: handleSubmit,
        },
      ]}
    >
      <MiDetailFields detailFields={fields} control={control} />
    </MiModal>
  );
};
